/**
 * Les éléments ouverts récemment depuis la recherche globale — une commodité de POSTE (localStorage),
 * jamais une donnée : sa lecture peut échouer ou revenir vide sans conséquence.
 *
 * <p><b>Cloisonnés comme la recherche</b> : une liste par utilisateur ET par espace. Un Président ne
 * retrouve pas dans l'espace du Conseil ce qu'il a ouvert dans celui de la Présidence, et deux agents
 * qui partagent un poste ne voient pas leurs listes respectives.
 *
 * <p><b>Rien de confidentiel.</b> Les notes d'alerte (classées `CONFIDENTIEL` par défaut) ne sont
 * JAMAIS retenues : l'objet d'une note n'a rien à faire en clair dans le stockage d'un navigateur.
 * Toutes les listes sont effacées à la déconnexion ({@link effacerRecents}). Et une entrée n'est qu'un
 * lien : l'ouvrir repasse par la garde de route puis par l'`AccessGuard` du serveur.
 */

export interface Recent {
  readonly domId: string;
  readonly libelle: string;
  readonly detail: string | null;
  readonly icone?: string;
  readonly route: string;
  readonly query?: Readonly<Record<string, string>>;
}

const PREFIXE = 'cce.recherche.recents.';
const MAX = 6;
/** Catégories jamais retenues (préfixe de `domId`). */
const EXCLUES = ['rg-NOTE_ALERTE-'];

function cle(utilisateur: string, espace: string): string {
  return `${PREFIXE}${utilisateur}.${espace}`;
}

export function lireRecents(utilisateur: string | null, espace: string | null): Recent[] {
  if (!utilisateur || !espace) return [];
  try {
    const brut = JSON.parse(localStorage.getItem(cle(utilisateur, espace)) ?? '[]');
    return Array.isArray(brut)
      ? brut.filter((r): r is Recent => typeof r?.domId === 'string' && typeof r?.route === 'string').slice(0, MAX)
      : [];
  } catch {
    return [];
  }
}

/** Place `r` en tête de la liste (sans doublon) et la renvoie. */
export function retenirRecent(utilisateur: string | null, espace: string | null, r: Recent): Recent[] {
  if (!utilisateur || !espace || EXCLUES.some((p) => r.domId.startsWith(p))) {
    return lireRecents(utilisateur, espace);
  }
  const liste = [r, ...lireRecents(utilisateur, espace).filter((x) => x.domId !== r.domId)].slice(0, MAX);
  try {
    localStorage.setItem(cle(utilisateur, espace), JSON.stringify(liste));
  } catch {
    // Stockage indisponible (navigation privée, quota) : la liste vaut pour la session.
  }
  return liste;
}

/** À la déconnexion : toutes les listes, de tous les utilisateurs du poste. */
export function effacerRecents(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIXE)) localStorage.removeItem(k);
    }
  } catch {
    // Rien à effacer si le stockage est inaccessible.
  }
}
